import { api } from 'bxcommon/store/api';
import { hydrateList, updateOnList } from 'bxcommon/store/slices/list.slice';
import { updateRequestField } from '../slices/request.slice';
import errorService from 'bxcommon/helpers/errors.helper';

export const ADVISOR_PAGE_SIZE = 50;

const registryApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAdvisorList: build.query({
      query: ({ search, query, page = 1 } = {}) => ({
        url: '/client-advisors/',
        params: { page, page_size: ADVISOR_PAGE_SIZE, ...(search ? { [query]: search } : {}) },
      }),
      transformResponse: (response) => response.data,
      providesTags: ['List'],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(hydrateList(data.results));
        } catch {
          // handled by interceptor
        }
      },
    }),

    deleteAdvisor: build.mutation({
      query: (caseId) => ({ url: `/client-advisors/${caseId}/`, method: 'DELETE' }),
      invalidatesTags: ['List'],
    }),

    submitRequest: build.mutation({
      query: (data) => {
        const caseId = data.case_id ? `${data.case_id}/` : '';
        return { url: `/client-advisors/${caseId}`, method: 'PATCH', data };
      },
      transformResponse: (response) => response.data,
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(updateRequestField(data));
          dispatch(updateOnList(data));
        } catch (error) {
          errorService.backendValidationError(error);
        }
      },
    }),

    createRequest: build.mutation({
      query: (data) => ({ url: '/client-advisors/', method: 'POST', data }),
      transformResponse: (response) => response.data,
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(updateRequestField(data));
          dispatch(updateOnList(data));
        } catch (error) {
          errorService.backendValidationError(error);
        }
      },
    }),

    updateRequest: build.mutation({
      query: (data) => {
        const caseId = data.case_id ? `${data.case_id}/` : '';
        return { url: `/client-advisors/${caseId}`, method: 'PUT', data };
      },
      transformResponse: (response) => response.data,
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(updateRequestField(data));
          dispatch(updateOnList(data));
        } catch (error) {
          errorService.backendValidationError(error);
        }
      },
    }),

    deleteRequest: build.mutation({
      query: (caseId) => ({ url: `/client-advisors/${caseId}/`, method: 'DELETE' }),
    }),

    restoreCompanies: build.mutation({
      query: ({ caseId, companiesData, url }) => ({
        url: `/${url}/${caseId}/restore-companies/`,
        method: 'POST',
        data: companiesData,
      }),
      transformResponse: (response) => response.data,
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(updateOnList(data));
        } catch (error) {
          errorService.backendValidationError(error);
        }
      },
    }),

    togglePublish: build.mutation({
      query: ({ publish, caseId }) => ({
        url: `/client-advisors/${caseId}/${publish ? 'publish' : 'unpublish'}/`,
        method: 'POST',
      }),
      onQueryStarted: async ({ publish }, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(updateRequestField({ is_published: publish }));
      },
    }),

    getCsv: build.mutation({
      query: (payload) => ({ url: '/client-advisors/csv-list/', method: 'POST', data: payload }),
    }),

    updateAdditionalRecipients: build.mutation({
      query: (payload) => ({ url: '/client-advisors/additional-recipients/', method: 'POST', data: payload }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(updateRequestField({ additional_recipients: arg.new_additional_recipients }));
      },
    }),
  }),
});

export const {
  useGetAdvisorListQuery,
  useLazyGetAdvisorListQuery,
  useDeleteAdvisorMutation,
  useSubmitRequestMutation,
  useCreateRequestMutation,
  useUpdateRequestMutation,
  useDeleteRequestMutation,
  useRestoreCompaniesMutation,
  useTogglePublishMutation,
  useGetCsvMutation,
  useUpdateAdditionalRecipientsMutation,
} = registryApi;
